import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useChartPngRegistry, ChartPngPageIdOverrideContext } from '../../contexts/ChartPngRegistryContext';
import { useUserRole } from '../../contexts/UserRoleContext';
import { DataService } from '../../services/dataService';
import { downloadAllDataAsZip, downloadDataAsCSV, formatFilename } from '../../utils/downloadUtils';
import { printPageReport, downloadReportAsWord, REPORT_PAGE_LABELS, type ReportPageId } from '../../utils/pageReportToPdf';
import { Transportation } from '../pages/Transportation';
import { Energy } from '../pages/Energy';
import { People } from '../pages/People';
import { Other } from '../pages/Other';
import './DataDownloadModal.css';

/** Hidden container so off-screen pages don't affect layout. Charts inside still register with ChartPngRegistry. */
const HIDDEN_SHADOW_STYLE: React.CSSProperties = {
  position: 'fixed',
  left: -10000,
  top: 0,
  width: 1,
  height: 1,
  overflow: 'hidden',
  visibility: 'hidden' as const,
  pointerEvents: 'none',
  zIndex: -1
};

/** Page/section slug for Download Data tabs (must match ChartPngPageId for PNG filtering). */
export type DownloadModalPage = 'transportation' | 'buildings' | 'waste' | 'people' | 'overview';

const DOWNLOAD_TABS: { id: DownloadModalPage; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'transportation', label: 'Transportation' },
  { id: 'buildings', label: 'Energy & Buildings' },
  { id: 'waste', label: 'Waste' },
  { id: 'people', label: 'People' }
];

/** Map dataset section to tab id for filtering. Transportation data only on Transportation tab; Overview (e.g. Burdens and Benefits) only on Overview tab. */
function sectionToTab(section: string | undefined): DownloadModalPage {
  if (!section) return 'overview';
  if (section === 'Transportation') return 'transportation';
  if (section === 'Energy') return 'buildings';
  if (section === 'Waste') return 'waste';
  if (section === 'People & Demographics') return 'people';
  if (section === 'Overview') return 'overview';
  return 'overview';
}

interface DataDownloadModalProps {
  isOpen: boolean;
  onClose: () => void;
  location: string;
  geofips: number | null;
  /** Current page/section (from route) so we can default tab and show "chart PNGs for this page". */
  currentPage?: DownloadModalPage | string;
}

interface DataSet {
  name: string;
  description: string;
  data: any[];
  loading: boolean;
  error: string | null;
  section?: string;
}

const DataDownloadModal: React.FC<DataDownloadModalProps> = ({
  isOpen,
  onClose,
  location,
  geofips,
  currentPage
}) => {
  const { user, trackDatabaseDownload } = useAuth();
  const { hasAnyRole } = useUserRole();
  const { entries: pngEntries, triggerExport } = useChartPngRegistry();
  const stripEditUi = hasAnyRole(['super_admin', 'local_govt_admin']);
  const [dataSets, setDataSets] = useState<DataSet[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedDataSets, setSelectedDataSets] = useState<Set<string>>(new Set());
  const [selectedPngIds, setSelectedPngIds] = useState<Set<string>>(new Set());
  const [downloadingPngs, setDownloadingPngs] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [reportGenerating, setReportGenerating] = useState(false);
  const [activeTab, setActiveTab] = useState<DownloadModalPage>(() => {
    const p = currentPage as DownloadModalPage | undefined;
    return p && DOWNLOAD_TABS.some(t => t.id === p) ? p : 'overview';
  });

  const reportPageId = (currentPage === 'transportation' || currentPage === 'buildings' || currentPage === 'people')
    ? (currentPage as ReportPageId)
    : null;
  const reportPageLabel = reportPageId ? REPORT_PAGE_LABELS[reportPageId] : null;

  const handleDownloadPdfReport = async () => {
    setPdfError(null);
    if (!reportPageId) {
      setPdfError('PDF report is available only when viewing Transportation, Energy & Buildings, or People.');
      return;
    }
    setReportGenerating(true);
    try {
      const ok = await printPageReport(reportPageId, location, stripEditUi);
      if (!ok) {
        setPdfError('Could not open print dialog. Allow popups for this site, or try again while on this page.');
      }
    } finally {
      setReportGenerating(false);
    }
  };

  const handleDownloadWordReport = async () => {
    setPdfError(null);
    if (!reportPageId) {
      setPdfError('Word report is available only when viewing Transportation, Energy & Buildings, or People.');
      return;
    }
    setReportGenerating(true);
    try {
      const ok = await downloadReportAsWord(reportPageId, location, stripEditUi);
      if (!ok) {
        setPdfError('Could not generate Word document. Try again while on this page.');
      }
    } finally {
      setReportGenerating(false);
    }
  };

  const loadAllData = useCallback(async () => {
    if (!geofips) return;
    setLoading(true);
    setError(null);

    const dataSetConfigs = [
      { section: 'Transportation', name: 'VMT', description: 'Vehicle Miles Traveled (2005-2023)', loader: () => DataService.getVMTDataWithScaling(geofips) },
      { section: 'Transportation', name: 'VMT_Raw', description: 'VMT raw Replica by mode', loader: () => DataService.getVMTDataByGeofips(geofips) },
      { section: 'Transportation', name: 'VMT_Per_Capita', description: 'VMT per capita (location + CA average)', loader: () => DataService.getVMTPerCapitaData(geofips) },
      { section: 'Transportation', name: 'EMFAC', description: 'EMFAC backcasting (2005-2014)', loader: () => DataService.getEMFACBackcastingDataByGeofips(geofips) },
      { section: 'Overview', name: 'Burdens_and_Benefits', description: 'Equity indicators summary (Livability & Climate Equity Index)', loader: () => DataService.getBurdensAndBenefitsData(geofips) },
      { section: 'People & Demographics', name: 'Population', description: 'Population for this location by year', loader: () => DataService.getPopulationDataByGeofips(geofips) },
      { section: 'People & Demographics', name: 'Health', description: 'Health insurance (percent uninsured)', loader: () => DataService.getHealthInsuranceData(location) },
      { section: 'Energy', name: 'PV_Cost', description: 'Solar PV cost data', loader: () => DataService.getPvCostData(location) },
      { section: 'Energy', name: 'PV_Size', description: 'Solar PV size data', loader: () => DataService.getPvSizeData(location) }
    ];

    const dataSetsWithLoading = dataSetConfigs.map(config => ({
      name: config.name,
      description: config.description,
      data: [],
      loading: true,
      error: null,
      section: config.section
    }));

    setDataSets(dataSetsWithLoading);

    const loadedDataSets = await Promise.all(
      dataSetConfigs.map(async (config) => {
        try {
          const data = await config.loader();
          return {
            name: config.name,
            description: config.description,
            data: data || [],
            loading: false,
            error: null,
            section: config.section
          };
        } catch (err) {
          console.error(`Error loading ${config.name} data:`, err);
          return {
            name: config.name,
            description: config.description,
            data: [],
            loading: false,
            error: `Failed to load ${config.name} data`,
            section: config.section
          };
        }
      })
    );

    setDataSets(loadedDataSets);
    setLoading(false);
    // Default: nothing selected
  }, [geofips, location]);

  useEffect(() => {
    if (isOpen && user && geofips) {
      loadAllData();
    }
  }, [isOpen, user, geofips, loadAllData]);

  useEffect(() => {
    if (isOpen) setPdfError(null);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !currentPage) return;
    const tabId = currentPage === 'other' ? 'waste' : currentPage;
    if (DOWNLOAD_TABS.some((t) => t.id === tabId)) setActiveTab(tabId as DownloadModalPage);
  }, [isOpen, currentPage]);

  const handlePngToggle = (id: string) => {
    setSelectedPngIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleDownloadSelectedPNGs = () => {
    const ids = Array.from(selectedPngIds);
    if (ids.length === 0) {
      setError('Select at least one chart to download as PNG');
      return;
    }
    setDownloadingPngs(true);
    triggerExport(ids);
    setTimeout(() => setDownloadingPngs(false), 1500);
  };

  const handleDownloadOnePng = (id: string) => {
    triggerExport([id]);
  };

  const handleDataSetToggle = (dataSetName: string) => {
    const newSelected = new Set(selectedDataSets);
    if (newSelected.has(dataSetName)) {
      newSelected.delete(dataSetName);
    } else {
      newSelected.add(dataSetName);
    }
    setSelectedDataSets(newSelected);
  };

  const selectAllData = () => {
    const available = dataSets
      .filter((d) => sectionToTab(d.section) === activeTab && !d.loading && d.data.length > 0)
      .map((d) => d.name);
    setSelectedDataSets((prev) => {
      const next = new Set(prev);
      available.forEach((name) => next.add(name));
      return next;
    });
  };

  const selectAllPngs = () => {
    const ids = pngEntries
      .filter((e) => (activeTab === 'overview' ? (e.pageId === 'overview' || e.pageId == null) : e.pageId === activeTab))
      .map((e) => e.id);
    setSelectedPngIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.add(id));
      return next;
    });
  };

  const deselectAllData = () => {
    const inTab = dataSets.filter((d) => sectionToTab(d.section) === activeTab).map((d) => d.name);
    setSelectedDataSets((prev) => {
      const next = new Set(prev);
      inTab.forEach((name) => next.delete(name));
      return next;
    });
  };

  const deselectAllPngs = () => {
    const inTab = pngEntries
      .filter((e) => (activeTab === 'overview' ? (e.pageId === 'overview' || e.pageId == null) : e.pageId === activeTab))
      .map((e) => e.id);
    setSelectedPngIds((prev) => {
      const next = new Set(prev);
      inTab.forEach((id) => next.delete(id));
      return next;
    });
  };

  const handleDownloadSelected = async () => {
    const selectedData = dataSets
      .filter(ds => selectedDataSets.has(ds.name) && ds.data.length > 0)
      .reduce((acc, ds) => {
        acc[ds.name] = ds.data;
        return acc;
      }, {} as { [key: string]: any[] });

    if (Object.keys(selectedData).length === 0) {
      setError('Please select at least one dataset to download');
      return;
    }

    // Track the download
    const databaseNames = Object.keys(selectedData);
    for (const dbName of databaseNames) {
      await trackDatabaseDownload(dbName);
    }

    downloadAllDataAsZip(location, selectedData);
  };


  const handleDownloadIndividual = async (dataSet: DataSet) => {
    if (dataSet.data.length === 0) {
      setError(`No data available for ${dataSet.name}`);
      return;
    }

    // Track the download
    await trackDatabaseDownload(dataSet.name);

    const filename = formatFilename(location, dataSet.name);
    downloadDataAsCSV(dataSet.data, filename);
  };

  const filteredDataSets = dataSets.filter((d) => sectionToTab(d.section) === activeTab);
  // Section tabs (Transportation, Buildings, Waste, People) show only PNGs for that section; Overview shows overview + untagged
  const filteredPngEntries = pngEntries.filter((e) =>
    activeTab === 'overview'
      ? (e.pageId === 'overview' || e.pageId == null)
      : e.pageId === activeTab
  );

  if (!isOpen) return null;

  return (
    <div className="data-download-overlay" onClick={onClose}>
      {/* Mount all section pages off-screen when modal is open so every tab has CSV + PNG; they register with ChartPngRegistry. Use context for pageId so we don't nest another Router. */}
      {location && geofips && (
        <div style={HIDDEN_SHADOW_STYLE} aria-hidden="true">
          <ChartPngPageIdOverrideContext.Provider value="transportation">
            <Transportation location={location} />
          </ChartPngPageIdOverrideContext.Provider>
          <ChartPngPageIdOverrideContext.Provider value="buildings">
            <Energy location={location} />
          </ChartPngPageIdOverrideContext.Provider>
          <ChartPngPageIdOverrideContext.Provider value="people">
            <People location={location} />
          </ChartPngPageIdOverrideContext.Provider>
          <ChartPngPageIdOverrideContext.Provider value="waste">
            <Other />
          </ChartPngPageIdOverrideContext.Provider>
        </div>
      )}
      <div className="data-download-modal" onClick={(e) => e.stopPropagation()}>
        <div className="data-download-header">
          <h2>Download Data - {location}</h2>
          <button className="data-download-close" onClick={onClose}>×</button>
        </div>

        <div className="data-download-tabs">
          {DOWNLOAD_TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`data-download-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="data-download-body">
          {error && (
            <div className="data-download-error">
              {error}
            </div>
          )}

          <div className="data-download-info">
            <p>Each tab lists <strong>CSV data</strong> and <strong>chart PNGs</strong> for that page. All files are for the selected location only. Open a page (e.g. Transportation) to see its charts in that tab; CSV data is available for all tabs.</p>
            <p><strong>Location:</strong> {location} (FIPS: {geofips})</p>
          </div>

          <div className="data-download-actions">
            <button
              className="data-download-button data-download-button-primary"
              onClick={handleDownloadSelected}
              disabled={loading || selectedDataSets.size === 0}
            >
              Download Selected Data ({selectedDataSets.size})
            </button>
          </div>

          <div className="data-download-section-label">
            Page PDF report
          </div>
          <p className="data-download-pdf-description">
            Download the current page as a PDF (titles, key metrics, overview, relevance, equity, methods, charts, and maps). The print dialog will open—choose &quot;Save as PDF&quot; or a printer.
          </p>
          {pdfError && (
            <div className="data-download-error" style={{ marginBottom: 12 }}>
              {pdfError}
            </div>
          )}
          <div className="data-download-actions">
            <button
              type="button"
              className="data-download-button data-download-button-pdf"
              onClick={handleDownloadPdfReport}
              disabled={!reportPageId || reportGenerating}
              title={reportPageId ? `Generate PDF report for ${reportPageLabel}` : 'Go to Transportation, Energy & Buildings, or People to download a page PDF'}
            >
              {reportGenerating ? '⏳ Generating…' : reportPageId ? `📄 Download ${reportPageLabel} as PDF` : 'Page PDF (open a section page first)'}
            </button>
            <button
              type="button"
              className="data-download-button data-download-button-word"
              onClick={handleDownloadWordReport}
              disabled={!reportPageId || reportGenerating}
              title={reportPageId ? `Download ${reportPageLabel} as Word document` : 'Go to a section page to download as Word'}
            >
              {reportGenerating ? '⏳ Generating…' : reportPageId ? `📝 Download ${reportPageLabel} as Word` : 'Page Word (open a section page first)'}
            </button>
          </div>

          <div className="data-download-section-label">
            Data (CSV) – {DOWNLOAD_TABS.find((t) => t.id === activeTab)?.label ?? activeTab}
            <span className="data-download-section-actions">
              <button
                type="button"
                className="data-download-select-all"
                onClick={selectAllData}
                disabled={loading || filteredDataSets.filter((d) => d.data.length > 0).length === 0}
              >
                Select all
              </button>
              <button
                type="button"
                className="data-download-select-all"
                onClick={deselectAllData}
                disabled={filteredDataSets.every((d) => !selectedDataSets.has(d.name))}
              >
                Deselect all
              </button>
            </span>
          </div>
          <div className="data-download-list">
            {filteredDataSets.map((dataSet) => (
              <div key={dataSet.name} className="data-download-item">
                <div className="data-download-item-header">
                  <label className="data-download-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedDataSets.has(dataSet.name)}
                      onChange={() => handleDataSetToggle(dataSet.name)}
                      disabled={dataSet.loading || dataSet.data.length === 0}
                    />
                    <span className="data-download-checkmark"></span>
                    <div className="data-download-item-info">
                      {dataSet.section && <span className="data-download-section-tag">{dataSet.section}</span>}
                      <h4>{dataSet.name}</h4>
                      <p>{dataSet.description}</p>
                      {dataSet.data.length > 0 && (
                        <span className="data-download-count">
                          {dataSet.data.length} records (this location)
                        </span>
                      )}
                    </div>
                  </label>
                  
                  <button
                    className="data-download-button data-download-button-small"
                    onClick={() => handleDownloadIndividual(dataSet)}
                    disabled={dataSet.loading || dataSet.data.length === 0}
                  >
                    {dataSet.loading ? 'Loading...' : 'Download'}
                  </button>
                </div>

                {dataSet.loading && (
                  <div className="data-download-loading">
                    <div className="data-download-spinner"></div>
                    Loading {dataSet.name} data...
                  </div>
                )}

                {dataSet.error && (
                  <div className="data-download-item-error">
                    {dataSet.error}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="data-download-section-label">
            Chart PNGs – {DOWNLOAD_TABS.find((t) => t.id === activeTab)?.label ?? activeTab}
            {filteredPngEntries.length > 0 && (
              <span className="data-download-section-actions">
                <button
                  type="button"
                  className="data-download-select-all"
                  onClick={selectAllPngs}
                >
                  Select all
                </button>
                <button
                  type="button"
                  className="data-download-select-all"
                  onClick={deselectAllPngs}
                  disabled={filteredPngEntries.every((e) => !selectedPngIds.has(e.id))}
                >
                  Deselect all
                </button>
              </span>
            )}
          </div>
          {filteredPngEntries.length > 0 ? (
            <>
              <div className="data-download-actions data-download-actions-png">
                <button
                  className="data-download-button data-download-button-png"
                  onClick={handleDownloadSelectedPNGs}
                  disabled={downloadingPngs || selectedPngIds.size === 0}
                  title="Export selected charts as PNG"
                >
                  {downloadingPngs ? 'Triggering…' : `🖼️ Download Selected PNGs (${selectedPngIds.size})`}
                </button>
              </div>
              <div className="data-download-list">
                {filteredPngEntries.map((entry) => (
                  <div key={entry.id} className="data-download-item data-download-item-png">
                    <div className="data-download-item-header">
                      <label className="data-download-checkbox">
                        <input
                          type="checkbox"
                          checked={selectedPngIds.has(entry.id)}
                          onChange={() => handlePngToggle(entry.id)}
                        />
                        <span className="data-download-checkmark" />
                        <div className="data-download-item-info">
                          <h4>{entry.label}</h4>
                        </div>
                      </label>
                      <button
                        className="data-download-button data-download-button-small"
                        onClick={() => handleDownloadOnePng(entry.id)}
                        title={`Download ${entry.label} as PNG`}
                      >
                        Download
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <p className="data-download-png-empty">
              {activeTab === 'overview'
                ? 'No chart PNGs for Overview. Chart images are listed in the Transportation, Energy & Buildings, Waste, and People tabs above.'
                : `No chart PNGs for this tab. Charts from the ${DOWNLOAD_TABS.find((t) => t.id === activeTab)?.label ?? activeTab} page appear here when the modal is open.`}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default DataDownloadModal;
